import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlannedInvestment, Project } from "@/types";
import { useBatchData } from "./useBatchData";
import { fetchProjectMeasures } from "@/services/api";

import { Measure } from "@/types";

type DocumentedMeasure = Pick<Measure, "category" | "totalNet">;

export function useProjectsBatchData(
  projects: Project[],
  projectToBatchIds: Record<string, string[]>,
  batchTitles: Record<string, string>
) {
  // Get all batch IDs for all projects (planned investments only - those
  // still belong to a batch/vintage)
  const allBatchIds = useMemo(() => {
    const batchIds = new Set<string>();
    projects.forEach((project) => {
      const projectBatchIds = projectToBatchIds[project.id] || [];
      projectBatchIds.forEach((id) => batchIds.add(id));
    });
    return Array.from(batchIds);
  }, [projects, projectToBatchIds]);

  // Get planned investments data for all projects at once
  const { projectMeasurements } = useBatchData(
    projects.length > 0 ? projects[0].id : null,
    allBatchIds,
    batchTitles
  );

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);

  // Measures belong to each project directly (not to a batch/vintage), so
  // they're fetched once per project instead of derived from the shared
  // batch list above.
  const { data: measuresByProjectId } = useQuery({
    queryKey: ["projects-measures", projectIds],
    queryFn: async () => {
      const results = await Promise.all(
        projectIds.map((id) =>
          fetchProjectMeasures(id).catch(() => [] as DocumentedMeasure[])
        )
      );
      return projectIds.reduce(
        (acc, id, index) => {
          acc[id] = results[index];
          return acc;
        },
        {} as Record<string, DocumentedMeasure[]>
      );
    },
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const projectsMeasurements = useMemo(() => {
    const measurements: Record<
      string,
      { planned: PlannedInvestment[]; documented: DocumentedMeasure[] }
    > = {};
    projects.forEach((project) => {
      const projectBatchIds = projectToBatchIds[project.id] || [];
      measurements[project.id] = {
        planned: projectMeasurements.planned.filter((measure) =>
          projectBatchIds.includes(measure.batchId)
        ),
        documented: measuresByProjectId?.[project.id] ?? [],
      };
    });
    return measurements;
  }, [projects, projectToBatchIds, projectMeasurements, measuresByProjectId]);

  return {
    projectsMeasurements,
  };
}
